import { XMarkIcon } from "@heroicons/react/20/solid";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { readPersonPanel } from "../../../api/access/accessApi";
import type { AccessList } from "../../../api/access/accessModel";
import { useLoad } from "../../../shared/loading/useLoad";
import PersonAvatar from "../../../ui/avatars/PersonAvatar";
import IconButton from "../../../ui/buttons/IconButton";
import Chip from "../../../ui/feedback/Chip";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { itemKindText } from "../../workflows";
import { groupNamer, type GroupNamer } from "../model/groupNames";
import { panelRows, type PanelRow } from "./panelRows";

interface PersonPanelProps {
  projectId: string;
  userId: string;
  /** The project's list, which names its groups. */
  list: AccessList;
  onClose: () => void;
}

/**
 * One person's access in a project, in one answer (redesign R3-2 "The access panel", GS-6):
 * each permission ✓ with the groups and paths that give it, or – with the groups that have
 * it; Curate with its kinds; and who can change this person's access. Every answer is the
 * server's `assess`; nothing is worked out here.
 */
export default function PersonPanel({ projectId, userId, list, onClose }: PersonPanelProps) {
  const { t, i18n } = useTranslation();
  const load = useCallback((signal: AbortSignal) => readPersonPanel(projectId, userId, signal), [projectId, userId]);
  const { state, reload } = useLoad(load);
  const listed = list.people.find((person) => person.userId === userId);
  const known = list.namedPeople.get(userId);

  return (
    <section className="app-card min-w-0 space-y-4 p-4" aria-labelledby="access-person-title">
      <header className="flex items-start gap-3">
        <PersonAvatar userId={userId} name={known?.displayName ?? ""} />
        <div className="min-w-0 flex-1">
          <h3 id="access-person-title" className="app-title truncate text-base font-semibold">
            {state.status === "loaded" ? state.value.person.displayName : known?.displayName}
          </h3>
          <p className="app-text-muted truncate text-xs">{state.status === "loaded" ? state.value.person.email : known?.email}</p>
          {state.status === "loaded" && !state.value.person.active ? <Chip tone="warning">{t("access.panel.inactive")}</Chip> : null}
        </div>
        <IconButton label={t("access.panel.close")} onClick={onClose}><XMarkIcon className="h-4 w-4" aria-hidden="true" /></IconButton>
      </header>
      {state.status !== "loaded" ? <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} /> : (() => {
        const namer = groupNamer(t, list, state.value.namedGroups);
        return (
          <>
            <div className="space-y-1">
              <h4 className="app-label text-xs">{t("access.panel.groupsHere")}</h4>
              <p className="flex flex-wrap gap-1">
                {(listed?.groupIds ?? []).length === 0 ? <span className="app-text-muted text-sm">{t("access.panel.noGroup")}</span> : null}
                {listed?.groupIds.map((groupId) => <Chip key={groupId}>{namer.name(groupId)}</Chip>)}
              </p>
            </div>
            <div className="space-y-1">
              <h4 className="app-label text-xs">{t("access.panel.inThisProject")}</h4>
              <ul className="space-y-1.5 text-sm">
                {panelRows(state.value.lines).map((row) => <Row key={row.permission} row={row} namer={namer} />)}
              </ul>
            </div>
            <p className="app-divider-top app-text-muted pt-3 text-sm">
              {state.value.managers.length === 0
                ? t("access.panel.managedByAdministrators")
                : t("access.panel.managedBy", {
                  count: state.value.managers.length,
                  names: new Intl.ListFormat(i18n.resolvedLanguage, { type: "conjunction" })
                    .format(state.value.managers.map((name) => name ?? t("permissionHistory.removedAccount"))),
                })}
            </p>
          </>
        );
      })()}
    </section>
  );
}

function Row({ row, namer }: { row: PanelRow; namer: GroupNamer }) {
  const { t } = useTranslation();
  const label = row.permission === "curate" && row.allowed
    ? t("access.panel.curates", { kinds: row.kinds.map((kind) => itemKindText(kind, t)).join(", ") })
    : t(`access.permissions.${row.permission}`);
  return (
    <li className="flex items-start gap-2">
      <span className={row.allowed ? "font-semibold" : "app-text-muted"} aria-hidden="true">{row.allowed ? "✓" : "–"}</span>
      <span className="min-w-0 flex-1">
        <span className="sr-only">{t(row.allowed ? "access.panel.yes" : "access.panel.no")} </span>
        <span className={row.allowed ? undefined : "app-text-muted"}>{label}</span>
        {row.otherKinds > 0 ? <span className="app-text-muted block text-xs">{t("access.panel.notOtherKinds", { count: row.otherKinds })}</span> : null}
      </span>
      <span className="flex max-w-[50%] flex-wrap justify-end gap-1 text-xs">
        <Why row={row} namer={namer} />
      </span>
    </li>
  );
}

/** What gives it, or what would: the groups and their paths, "here", another source, or a block. */
function Why({ row, namer }: { row: PanelRow; namer: GroupNamer }) {
  const { t } = useTranslation();
  if (row.allowed) {
    return (
      <>
        {row.sources.map((source) => (
          <Chip key={`${source.path.join("/")}:${String(source.here)}`}>
            {source.here ? t("access.panel.here", { group: namer.path(source.path) }) : namer.path(source.path)}
          </Chip>
        ))}
        {row.allowedBy === null ? null : <Chip>{t(`access.allowedBy.${row.allowedBy}`)}</Chip>}
      </>
    );
  }
  if (row.block !== null) return <span className="app-text-muted">{t(`access.blocks.${row.block}`)}</span>;
  if (row.withIt.length === 0) return null;
  return <span className="app-text-muted">{t("access.panel.haveIt", { groups: row.withIt.map((groupId) => namer.name(groupId)).join(", "), count: row.withIt.length })}</span>;
}
